import React, { memo } from 'react'
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Receipt, 
  Hammer 
} from 'lucide-react'

/**
 * Optimierte LawCard-Komponente mit React.memo
 * 
 * Verhindert unnötige Re-Renders für Gesetze/Politik-Karten.
 */
const LawCard = memo(({ 
  law,
  currentMonth,
  startDate
}) => {
  // Berechne verbleibende Monate (kompatibel mit bestehender Logik)
  const totalMonths = law.monthsRemaining ?? 12
  const remainingMonths = law.monthsRemaining ?? 12
  const progressPercent = (remainingMonths / totalMonths) * 100
  
  // Farbcodierung nach Effekt (kompatibel mit bestehender Logik)
  const hasPositiveEffect = (law.effect?.rentModifier && law.effect.rentModifier > 0) || 
                           (law.effect?.taxModifier && law.effect.taxModifier < 0) ||
                           (law.effect?.valueBoost && law.effect.valueBoost > 0)
  const hasNegativeEffect = (law.effect?.rentModifier && law.effect.rentModifier < 0) || 
                            (law.effect?.taxModifier && law.effect.taxModifier > 0) ||
                            (law.effect?.constructionCost || law.effect?.constructionSlowdown || law.effect?.costIncrease)
  
  let cardBorder = 'border-white/5'
  let cardHover = 'hover:border-accent/30'
  let iconBg = 'bg-accent/20'
  let iconColor = 'text-accent'
  let progressColor = 'from-accent to-emerald-400'
  
  if (hasPositiveEffect) {
    cardBorder = 'border-emerald-500/30'
    cardHover = 'hover:border-emerald-500/50'
    iconBg = 'bg-emerald-500/20'
    iconColor = 'text-emerald-400'
    progressColor = 'from-emerald-500 to-emerald-400'
  } else if (hasNegativeEffect) {
    cardBorder = 'border-red-500/30'
    cardHover = 'hover:border-red-500/50'
    iconBg = 'bg-red-500/20'
    iconColor = 'text-red-400'
    progressColor = 'from-red-500 to-red-400'
  }
  
  // Icon basierend auf Gesetzestyp
  let lawIcon = <TrendingUp className={iconColor} size={20} />
  if (law.effect?.rentModifier) {
    lawIcon = <DollarSign className={iconColor} size={20} />
  } else if (law.effect?.taxModifier) {
    lawIcon = <Receipt className={iconColor} size={20} />
  } else if (law.effect?.constructionCost || law.effect?.constructionSlowdown || law.effect?.costIncrease) {
    lawIcon = <Hammer className={iconColor} size={20} />
  }
  
  return (
    <div
      key={law.instanceId ?? law.id}
      className={`rounded-lg border ${cardBorder} bg-slate-900/70 p-2.5 shadow-md ${cardHover} transition`}
    >
      <div className="flex items-start gap-2">
        <div className={`rounded-lg ${iconBg} p-1.5`}>
          {lawIcon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <h4 className="text-sm font-semibold text-white truncate">{law.type}</h4>
            <span className="text-xs font-semibold text-slate-300 ml-2">{remainingMonths}M</span>
          </div>
          <p className="text-xs text-slate-300 mb-2 line-clamp-2">{law.description}</p>
          <div className="mt-2">
            <div className="flex justify-between text-[10px] text-slate-500 mb-0.5">
              <span>Laufzeit</span>
              <span>{Math.round(progressPercent)}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-slate-700 overflow-hidden">
              <div
                className={`h-full rounded-full bg-gradient-to-r ${progressColor} transition-all duration-300`}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}, (prevProps, nextProps) => {
  // Custom comparison function
  return (
    prevProps.law.instanceId === nextProps.law.instanceId &&
    prevProps.law.id === nextProps.law.id &&
    prevProps.currentMonth === nextProps.currentMonth &&
    prevProps.law.startMonth === nextProps.law.startMonth &&
    prevProps.law.duration === nextProps.law.duration
  )
})

LawCard.displayName = 'LawCard'

export default LawCard
